export const MC_CONTENT = {
  hero: {
    kicker: 'Para grupos de empresas, franquias e mais de um CNPJ',
    title: 'Cansado de logar em sistemas diferentes para cada empresa do seu grupo?',
    subtitle:
      'Cadastre todas as suas empresas em um único login, com troca rápida entre elas e permissão de usuário configurável empresa por empresa.',
    cta: 'Quero um login para todas as empresas',
    ctaMessage: 'Quero conhecer o sistema multiempresa do 77Gestão',
    urgency: 'Cada empresa em um sistema separado é mais uma senha, mais um acesso e mais uma chance de bagunçar a permissão de alguém.',
  },

  statsStrip: [
    { number: '1 login', label: 'para múltiplas empresas cadastradas', icon: 'fa-solid fa-right-to-bracket' },
    { number: 'Troca rápida', label: 'entre empresas ativas', icon: 'fa-solid fa-arrows-rotate' },
    { number: 'Por empresa', label: 'permissão de usuário configurável', icon: 'fa-solid fa-user-shield' },
  ],

  pain: {
    title: 'Reconhece alguma dessas situações no seu grupo de empresas?',
    intro:
      'Quem tem mais de um CNPJ sabe: a dor não é só ter mais de uma empresa, é ter que gerenciar acesso e sistema de forma separada para cada uma.',
    items: [
      { icon: 'fa-solid fa-right-to-bracket', text: 'Você (ou sua equipe) precisa logar em sistemas separados para cada empresa do grupo, com senhas e telas diferentes.' },
      { icon: 'fa-solid fa-user-plus', text: 'Dar acesso de um funcionário a mais de uma empresa do grupo vira um processo manual, cheio de gambiarra.' },
      { icon: 'fa-solid fa-user-shield', text: 'Permissão de usuário fica bagunçada entre as empresas — alguém acaba vendo (ou mexendo) em dados que não deveria.' },
      { icon: 'fa-solid fa-arrows-rotate', text: 'Trocar de contexto para olhar outra empresa do grupo significa fechar um sistema e abrir outro, perdendo tempo.' },
      { icon: 'fa-solid fa-building-circle-exclamation', text: 'Nova empresa aberta no grupo significa começar a configuração de sistema do zero, sem aproveitar nada da estrutura já existente.' },
    ],
  },

  midCta: {
    text: 'E se você pudesse entrar com um único login e trocar de empresa em poucos cliques, sem sair do sistema?',
    cta: 'Quero ver na prática',
    ctaMessage: 'Quero ver uma demonstração do sistema multiempresa do 77Gestão',
  },

  solution: {
    title: 'Todas as suas empresas, um único login, permissões sob seu controle.',
    intro:
      'O 77Gestão é nativamente multiempresa: um mesmo cadastro de acesso pode operar várias empresas, cada uma com seus próprios dados e permissões.',
    cards: [
      {
        icon: 'fa-solid fa-building',
        title: 'Múltiplas empresas sob um único login',
        text: 'Cadastre quantas empresas o seu grupo tiver e acesse todas com o mesmo usuário, sem precisar de login separado para cada CNPJ.',
      },
      {
        icon: 'fa-solid fa-arrows-rotate',
        title: 'Troca rápida entre empresas',
        text: 'Alterne entre as empresas ativas do grupo em poucos cliques, sem sair do sistema ou perder o que estava fazendo.',
      },
      {
        icon: 'fa-solid fa-user-shield',
        title: 'Permissões configuráveis por empresa',
        text: 'Defina o que cada usuário pode ver e fazer em cada empresa separadamente — um funcionário pode ter acesso amplo em uma empresa e restrito em outra.',
      },
      {
        icon: 'fa-solid fa-sitemap',
        title: 'Cada empresa com sua própria operação',
        text: 'Estoque, vendas e financeiro de cada empresa permanecem separados e organizados dentro do próprio cadastro dela.',
      },
      {
        icon: 'fa-solid fa-user-plus',
        title: 'Onboarding de nova empresa mais rápido',
        text: 'Ao abrir uma nova empresa no grupo, o cadastro entra na mesma estrutura de acesso já existente, sem começar do absoluto zero.',
      },
    ],
  },

  proof: {
    title: 'O que muda quando o grupo para de operar em sistemas separados',
    intro:
      'Um empresário com três empresas do mesmo grupo — cada uma com CNPJ próprio — mantinha logins e sistemas diferentes para cada uma, e dar acesso a um novo funcionário em mais de uma empresa era um processo manual e demorado.',
    stats: [
      { number: '1', label: 'login para acessar todas as empresas do grupo' },
      { number: 'Poucos cliques', label: 'para trocar de empresa ativa' },
      { number: 'Por empresa', label: 'controle de permissão de cada usuário' },
    ],
    paragraphs: [
      'Depois da implantação, o acesso às três empresas passou a ser feito com um único login, com troca rápida entre elas conforme a necessidade do dia a dia.',
      'A permissão de cada usuário passou a ser configurada empresa por empresa, dando segurança para liberar acesso a mais de uma empresa do grupo sem perder controle sobre o que cada um pode ver ou fazer. Importante: cada empresa manteve seu financeiro, estoque e vendas próprios e separados — a troca é de contexto de acesso, não uma junção dos números das empresas.',
    ],
    highlight: 'Resultado: um login para o grupo inteiro, troca rápida entre empresas e permissão organizada por empresa.',
    cta: 'Quero esse nível de controle',
    ctaMessage: 'Vi o resultado e quero entender como funciona o multiempresa no 77Gestão',
  },

  benefits: {
    title: 'Os três pilares que mudam a gestão de um grupo de empresas',
    items: [
      {
        icon: 'fa-solid fa-eye',
        title: 'Visibilidade',
        text: 'Veja rapidamente em qual empresa você está operando e alterne para outra sem sair do sistema.',
      },
      {
        icon: 'fa-solid fa-sliders',
        title: 'Controle',
        text: 'Permissão de cada usuário configurada empresa por empresa, sem depender de gambiarra para liberar acesso.',
      },
      {
        icon: 'fa-solid fa-binoculars',
        title: 'Previsibilidade',
        text: 'Nova empresa do grupo entra na mesma estrutura de acesso já existente, sem processo de configuração do zero.',
      },
    ],
  },

  differentials: {
    title: 'Por que grupos de empresas escolhem o 77Gestão',
    items: [
      { icon: 'fa-solid fa-sitemap', text: 'Multiempresa/multi-tenant nativo: não é uma gambiarra de acesso, é estrutura do próprio sistema.' },
      { icon: 'fa-solid fa-user-shield', text: 'Permissão de usuário configurável empresa por empresa, dentro do mesmo login.' },
      { icon: 'fa-solid fa-handshake', text: 'Implantação acompanhada: cada empresa do grupo é cadastrada e configurada com apoio da equipe.' },
      { icon: 'fa-solid fa-headset', text: 'Suporte humano, próximo e muito bem avaliado.' },
    ],
  },

  faq: {
    title: 'Perguntas comuns antes de começar',
    items: [
      {
        question: 'O financeiro das minhas empresas fica consolidado automaticamente?',
        answer:
          'Não. Cada empresa mantém seu próprio financeiro, estoque e vendas separados. O que existe é a troca rápida entre as empresas usando o mesmo login — não uma consolidação automática dos números do grupo.',
      },
      {
        question: 'Consigo dar acesso de um funcionário a mais de uma empresa do grupo?',
        answer:
          'Sim. Com um único login, o usuário pode ter acesso a mais de uma empresa cadastrada, e a permissão dele é configurada separadamente para cada uma.',
      },
      {
        question: 'Isso serve para franquia, com várias unidades de CNPJ próprio?',
        answer:
          'Sim. O modelo multiempresa foi pensado exatamente para grupos com mais de uma empresa ou filial com CNPJ próprio, incluindo redes de franquia.',
      },
      {
        question: 'Se eu abrir uma nova empresa no grupo, preciso configurar tudo de novo?',
        answer:
          'A nova empresa é cadastrada dentro da mesma estrutura de acesso já existente, o que agiliza o processo — mas cada empresa mantém seus próprios dados operacionais.',
      },
      {
        question: 'Quantas empresas consigo cadastrar sob o mesmo login?',
        answer:
          'O sistema foi pensado para múltiplas empresas por login, adaptando-se ao tamanho do grupo — de duas empresas a estruturas maiores.',
      },
    ],
  },

  ctaFinal: {
    title: 'Pare de logar em sistemas separados para cada empresa do seu grupo.',
    subtitle:
      'Agende uma demonstração e veja, na prática, como acessar todas as suas empresas com um único login e permissões configuráveis para cada uma.',
    cta: 'Agendar demonstração agora',
    ctaMessage: 'Quero agendar uma demonstração do sistema multiempresa do 77Gestão',
  },

  seo: {
    title: 'Sistema Multiempresa — ERP para Múltiplas Empresas | 77Gestão',
    description:
      'ERP multiempresa: cadastre várias empresas sob um único login, com troca rápida entre elas e permissões de usuário configuráveis empresa por empresa. Agende uma demonstração.',
  },
};
